import URI from 'urijs';

import { DEFAULT_INTERVAL } from 'services/Polling/Poller';
import { PollingService } from 'services/Polling/Polling';
import { APIList, OpenAPIJobResponse } from 'types';
import { STATUSES } from 'types/jobs';
import { APIError } from 'utils/errors';

import { postRequest } from './apiRequests';

// TODO: move to shared packages and replace the `events` shared package with ciq utils

export type AggregationStatusRequestType = {
  pollerName: string;
  jobAggregationsId?: string;
  onSuccess: Function;
  onFailure: Function;
  onPending?: Function;
  url?: string;
  interval?: number;
  enableBackoffInterval?: boolean;
  queryParams?: URLSearchParams;
};

export type AggregateJobErrorResponseType = {
  data: Array<{
    details: {
      error_message: string;
    };
  }>;
  failed_jobs_count: number;
};

export type AggregateJobSuccessResponseType = {
  data: Array<{
    details: {
      success_message: string;
    };
  }>;
};

type PollerErrorResponse = {
  error?: Error;
  retryCount?: number;
  maxRetry?: number;
  isTerminalError?: boolean;
};

type ResponseType = PollerErrorResponse & {
  status?: STATUSES;
};

type JobAggregationResponse = APIList<OpenAPIJobResponse> & ResponseType;
type JobPollingResponse = OpenAPIJobResponse & PollerErrorResponse;

export enum AsyncPollerErrorCode {
  JOB_FAILURE = 'job_failure',
  STATUS_UNAVAILABLE = 'status_unavailable',
}

export class AsyncPollerError extends Error {
  code: AsyncPollerErrorCode;

  status?: number;

  constructor({ code, message, status }: { code: AsyncPollerErrorCode; message: string; status?: number }) {
    super(message);
    this.name = 'AsyncPollerError';
    this.code = code;
    this.status = status;
  }
}

const jobAggregationsUrl = '/api/v1/job-aggregations';
const jobsUrl = '/api/v1/jobs';

const getAsyncPollerErrorMessage = (pollerName: string, error?: Error) =>
  error?.message || `Unable to verify job status: ${pollerName}`;

const createStatusUnavailableError = (pollerName: string, error?: Error) =>
  new AsyncPollerError({
    code: AsyncPollerErrorCode.STATUS_UNAVAILABLE,
    message: getAsyncPollerErrorMessage(pollerName, error),
    status: error instanceof APIError ? error.status : undefined,
  });

const createJobFailureError = (
  pollerName: string,
  details?: {
    error_type?: string;
    error_message?: string;
  },
) =>
  new AsyncPollerError({
    code: AsyncPollerErrorCode.JOB_FAILURE,
    message: details?.error_message || details?.error_type || `Failed to fetch: ${pollerName}`,
  });

const shouldRejectPollingError = (response?: PollerErrorResponse) => {
  if (!response?.error) {
    return false;
  }

  if (response.error instanceof APIError && [401, 403].includes(response.error.status)) {
    return true;
  }

  return !!response.isTerminalError;
};

const start = async (
  csrfToken: string,
  pollerName: string,
  url: string,
  params: any,
  callback: Function,
  cancelUrl?: string,
  data?: any,
): Promise<string | Error> => {
  const asyncUrlWithParams = new URI(url).addQuery(params);
  let requestID;

  // initial request, skip if request id is already provided
  if (!params?.request_id) {
    try {
      const response = await postRequest<any, any>(String(asyncUrlWithParams), data || null, csrfToken);
      requestID = response.request_id;
    } catch (error: any) {
      if (typeof callback === 'function') {
        callback(null, error);
      }

      return error;
    }
  } else {
    requestID = params.request_id;
  }

  if (!requestID) {
    throw new Error('missing request_id for async records');
  }

  // start poller
  const pollingOptions = {
    name: pollerName,
    url: String(new URI(url).addQuery({ request_id: requestID })),
    callback: (result: any) => {
      if (!result.status) {
        PollingService.destroy(pollerName);
        if (typeof callback === 'function') {
          callback(result);
        }
      }
    },
    requestID,
    enableBackoffInterval: true,
    cancelUrl,
    csrfToken,
  };
  PollingService.register(pollingOptions);
  return requestID;
};

const startJobAggregationsStatus = ({
  pollerName,
  jobAggregationsId,
  onSuccess,
  onFailure,
  onPending,
  url,
  interval,
  enableBackoffInterval = true,
  queryParams,
}: AggregationStatusRequestType) => {
  if (!jobAggregationsId && !url) {
    throw new Error('missing job_aggregations id for async tasks');
  }
  const deregisterPollerAndRunCallback = (response: ResponseType | JobAggregationResponse, callback?: Function) => {
    PollingService.destroy(pollerName);
    if (typeof callback === 'function') {
      callback(response);
    }
  };

  const pollingOptions = {
    name: pollerName,
    url:
      url ||
      `${jobAggregationsUrl}/${jobAggregationsId}/?include_tasks=true${
        queryParams ? `&${queryParams.toString()}` : ''
      }`,
    callback: (response: ResponseType) => {
      if (shouldRejectPollingError(response)) {
        deregisterPollerAndRunCallback(response, onFailure);
      } else if (response?.status === STATUSES.SUCCESS) {
        deregisterPollerAndRunCallback(response, onSuccess);
        // TODO: add branches for other statuses
      } else if (response?.status === STATUSES.FAILURE) {
        deregisterPollerAndRunCallback(response, onFailure);
      } else if (response?.status === STATUSES.PENDING) {
        onPending?.(response);
      } else if (response?.status === STATUSES.STARTED) {
        onPending?.(response);
      }
    },
    enableBackoffInterval,
    interval,
  };
  PollingService.register(pollingOptions);
};

const waitForJobAggregationsCompletion = (
  pollerName: string,
  jobAggregationsId: string,
  saltPollerName = true,
  interval?: number,
  enableBackoffInterval?: boolean,
  queryParams?: URLSearchParams,
) => {
  const saltedPollerName = saltPollerName ? `${pollerName}-${Date.now()}` : pollerName;

  return new Promise<OpenAPIJobResponse>((resolve, reject) => {
    startJobAggregationsStatus({
      pollerName: saltedPollerName,
      jobAggregationsId,
      onSuccess(jobAggregationResponse: APIList<OpenAPIJobResponse>) {
        resolve(jobAggregationResponse?.data[0]);
      },
      onFailure(jobAggregationResponse: JobAggregationResponse) {
        if (jobAggregationResponse?.error) {
          reject(createStatusUnavailableError(saltedPollerName, jobAggregationResponse.error));
          return;
        }

        const { error_type, error_message } = jobAggregationResponse?.data[0]?.details ?? {};
        reject(createJobFailureError(saltedPollerName, { error_type, error_message }));
      },
      interval: interval ?? DEFAULT_INTERVAL,
      enableBackoffInterval,
      queryParams,
    });
  });
};

const waitForJobCompletion = (
  pollerName: string,
  jobId: string,
  saltPollerName = true,
  interval?: number,
  enableBackoffInterval?: boolean,
) => {
  const saltedPollerName = saltPollerName ? `${pollerName}-${Date.now()}` : pollerName;

  return new Promise<OpenAPIJobResponse>((resolve, reject) => {
    const pollingOptions = {
      name: saltedPollerName,
      url: `${jobsUrl}/${jobId}/`,
      callback: (response: JobPollingResponse) => {
        if (shouldRejectPollingError(response)) {
          PollingService.destroy(saltedPollerName);
          reject(createStatusUnavailableError(saltedPollerName, response.error));
        } else if (response?.status === STATUSES.SUCCESS) {
          PollingService.destroy(saltedPollerName);
          resolve(response);
          // TODO: add branches for other statuses
        } else if (response?.status === STATUSES.FAILURE) {
          PollingService.destroy(saltedPollerName);
          const { error_type, error_message } = response.details ?? {};
          reject(createJobFailureError(saltedPollerName, { error_type, error_message }));
        }
      },
      enableBackoffInterval: enableBackoffInterval ?? true,
      interval: interval ?? DEFAULT_INTERVAL,
    };
    PollingService.register(pollingOptions);
  });
};

export const AsyncPollerService = {
  start,
  startJobAggregationsStatus,
  waitForJobAggregationsCompletion,
  waitForJobCompletion,
};
